/**
 * @fileoverview The loaded body of the Sales Performance screen.
 * @description Lays out what the summary answered, or one empty state when the scope has no
 * targets at all (`hasData` false): the KPI tiles, the two monthly charts, the team
 * bars, and the ranking and pending cards (which load their own lists for the same selection).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import PerformanceCard from '@/features/sales/components/PerformanceCard'
import PerformanceKpiStrip from '@/features/sales/components/PerformanceKpiStrip'
import PerformancePendingCard from '@/features/sales/components/PerformancePendingCard'
import PerformanceRankingCard from '@/features/sales/components/PerformanceRankingCard'
import PerformanceTeamBars from '@/features/sales/components/PerformanceTeamBars'
import PerformanceTrendCard from '@/features/sales/components/PerformanceTrendCard'
import TableEmptyState from '@/features/sales/components/TableEmptyState'
import { monthPoints } from '@/features/sales/lib/sales-performance.utils'

import type {
  SalesPerformanceQuery,
  SalesPerformanceSummary,
} from '@/features/sales/types/sales-performance.types'

interface SalesPerformanceContentProps {
  summary: SalesPerformanceSummary
  query: SalesPerformanceQuery
  isStale?: boolean
}

/**
 * @component
 * @param {SalesPerformanceContentProps} props - The summary to draw and the selection it is for.
 * @param {SalesPerformanceSummary} props.summary - The summary the backend answered.
 * @param {SalesPerformanceQuery} props.query - The selected period and team, for the two lists.
 * @param {boolean} [props.isStale] - Whether the summary is still the previous selection's, while
 * the new one loads; the figures are dimmed and marked busy.
 */
function SalesPerformanceContent({
  summary,
  query,
  isStale = false,
}: SalesPerformanceContentProps) {
  const { t, i18n } = useTranslation()

  if (!summary.hasData) {
    return (
      <section
        className="rounded-card border border-divider bg-white p-4 sm:p-5"
        data-testid="sales-performance-no-data"
      >
        <TableEmptyState
          message={t('sales.salesPerformance.empty.title')}
          description={t('sales.salesPerformance.empty.description')}
          icon="trend"
        />
      </section>
    )
  }

  return (
    <>
      <div
        className={`flex flex-col gap-4 transition-opacity ${isStale ? 'opacity-50' : ''}`}
        aria-busy={isStale}
        data-testid="sales-performance-figures"
      >
        <PerformanceKpiStrip kpis={summary.kpis} />

        <div className="grid gap-4 lg:grid-cols-2">
          <PerformanceTrendCard
            title={t('sales.salesPerformance.charts.achievedTitle')}
            subtitle={t('sales.salesPerformance.charts.achievedSubtitle')}
            points={monthPoints(summary.months, 'achieved', i18n.language)}
            colorClass="text-info"
            testId="sales-performance-achieved"
          />
          <PerformanceTrendCard
            title={t('sales.salesPerformance.charts.onboardedTitle')}
            subtitle={t('sales.salesPerformance.charts.onboardedSubtitle')}
            points={monthPoints(summary.months, 'onboarded', i18n.language)}
            colorClass="text-teal"
            testId="sales-performance-onboarded"
          />
        </div>

        <PerformanceCard
          title={t('sales.salesPerformance.teams.title')}
          subtitle={t('sales.salesPerformance.teams.subtitle')}
          testId="sales-performance-teams"
        >
          <PerformanceTeamBars teams={summary.teams} />
        </PerformanceCard>
      </div>

      <PerformanceRankingCard query={query} scopeKind={summary.scope.kind} />
      <PerformancePendingCard query={query} />
    </>
  )
}

export default SalesPerformanceContent
